import { Action } from '@ngrx/store';
import { getRowId} from './utilities';
import { TableState } from './table-definition';
import { IWithIdentifier } from '@digitaix/types';

export enum TableActionTypes {
  ArchiveRow = '[TableComponent] Archive Row',
  RestoreRow = '[TableComponent] Restore Row',
  DeleteRow = '[TableComponent] Delete Row',
  EditRow = '[TableComponent] Edit Row',
  ViewRow = '[TableComponent] View Row',
  AddRow = '[TableComponent] Add Row',
  DownloadRow = '[TableComponent] Download Row',
  ExportTableData = '[TableComponent] Export Table Data',
  ImportTableData = '[TableComponent] Import Table Data',
  RegisterTable = 'Register Table',
  RegisterTables = 'Register Tables',
  ToggleSorting = '[TableComponent] Toggle Sorting',
  ToggleFiltering = '[TableComponent] Toggle Filtering',
  TogglePagination = '[TableComponent] Toggle Pagination',
  ToggleDownload = '[TableComponent] Toggle Download',
  ToggleUpload = '[TableComponent] Toggle Upload',
  ToggleShowArchived = '[TableComponent] Toggle Show Archived',
  ToggleShowCreatedAt = '[TableComponent] Toggle Show CreatedAt',
  ToggleShowUpdatedAt = '[TableComponent] Toggle Show UpdatedAt',
  SetLoadingLimit = '[TableComponent] Set Loading Limit',
  SetPageSize = '[TableComponent] Set Page Size',
  SetPage = '[TableComponent] Set Page',
  LoadTableData = '[TableComponent] Load Table Data',
  SetTableLimit = '[TableComponent] Set Table Limit',
  SetExpandedRow = '[TableComponent] Set Expanded Row',
  SetExpandedRowEditMode = '[TableComponent] Set Expanded Row Edit Mode',
  SetExpandedRowFullMode = '[TableComponent] Set Expanded Row Full Mode',
  ToggleDialogEdit = '[TableComponent] Toggle Dialog Edit',
  ToggleDialogView = '[TableComponent] Toggle Dialog View',
  ClearExpandedRowId = '[TableComponent] Clear Expanded RowId',
  ToggleColumnHidden = '[TableComponent] Toggle Column Hidden',
  SelectRow = '[TableComponent] Select Row',
  SetCurrentTableTab = '[TableComponent] Set Current Table Tab',
  AddRowToSelection = '[TableComponent] Add Row To Selection',
  RemoveRowFromSelection = '[TableComponent] Remove Row From Selection',
  SetFilter = '[TableComponent] Set Filter',
  ClearSelectedRows = '@digitaix/core/tables/CLEAR_SELECTED_ROWS'
}

export class ClearSelectedRows implements Action {
  public readonly type = TableActionTypes.ClearSelectedRows;

  constructor(public tableId: string) {}
}

export class SetFilter<S extends string | boolean | number> implements Action {
  public readonly type = TableActionTypes.SetFilter;

  constructor(
    public tableId: string,
    public columnName: string,
    public filter: S
  ) {}
}

export class SetCurrentTableTab implements Action {
  public readonly type = TableActionTypes.SetCurrentTableTab;

  constructor(public tableId: string, public tableTab: number) {}
}

export class ToggleColumnHidden implements Action {
  public readonly type = TableActionTypes.ToggleColumnHidden;

  constructor(public tableId: string, public columnName: string) {}
}

export class ClearExpandedRowId implements Action {
  public readonly type = TableActionTypes.ClearExpandedRowId;

  constructor(public tableId: string) {}
}

export class AddRow implements Action {
  public readonly type = TableActionTypes.AddRow;

  constructor(public tableId: string, public collectionPath: string) {}
}

export class ToggleDialogEdit implements Action {
  public readonly type = TableActionTypes.ToggleDialogEdit;

  constructor(public tableId: string, public enabled?: boolean) {}
}

export class ToggleDialogView implements Action {
  public readonly type = TableActionTypes.ToggleDialogView;

  constructor(public tableId: string, public enabled?: boolean) {}
}

export class ToggleShowArchived implements Action {
  public readonly type = TableActionTypes.ToggleShowArchived;

  constructor(public tableId: string, public enabled?: boolean) {}
}

export class ToggleShowCreatedAt implements Action {
  public readonly type = TableActionTypes.ToggleShowCreatedAt;

  constructor(public tableId: string, public enabled?: boolean) {}
}

export class ToggleShowUpdatedAt implements Action {
  public readonly type = TableActionTypes.ToggleShowUpdatedAt;

  constructor(public tableId: string, public enabled?: boolean) {}
}

export class ToggleDownload implements Action {
  public readonly type = TableActionTypes.ToggleDownload;

  constructor(public tableId: string, public enabled?: boolean) {}
}

export class ToggleUpload implements Action {
  public readonly type = TableActionTypes.ToggleUpload;

  constructor(public tableId: string, public enabled?: boolean) {}
}

export class ExportTableData implements Action {
  public readonly type = TableActionTypes.ExportTableData;

  constructor(public tableId: string) {}
}

export class ImportTableData implements Action {
  public readonly type = TableActionTypes.ImportTableData;

  constructor(public tableId: string) {}
}

export class SetExpandedRowEditMode implements Action {
  public readonly type = TableActionTypes.SetExpandedRowEditMode;

  constructor(public tableId: string, public editMode?: boolean) {}
}

export class SetExpandedRowFullMode implements Action {
  public readonly type = TableActionTypes.SetExpandedRowFullMode;

  constructor(public tableId: string, public fullMode?: boolean) {}
}

export class SetTableLimit implements Action {
  public readonly type = TableActionTypes.SetTableLimit;

  constructor(public tableId: string, public limit: number) {}
}

export class LoadTableData implements Action {
  public readonly type = TableActionTypes.LoadTableData;

  constructor(public tableId: string, public collectionPath: string) {}
}

export class SetPageSize implements Action {
  public readonly type = TableActionTypes.SetPageSize;

  constructor(public tableId: string, public pageSize: number) {}
}

export class SetPage implements Action {
  public readonly type = TableActionTypes.SetPage;

  constructor(public tableId: string, public page: number) {}
}

export class SetLoadingLimit implements Action {
  public readonly type = TableActionTypes.SetLoadingLimit;

  constructor(public tableId: string, public limit: number) {}
}

export class ToggleSorting implements Action {
  public readonly type = TableActionTypes.ToggleFiltering;

  constructor(public tableId: string, public enabled?: boolean) {}
}

export class ToggleFiltering implements Action {
  public readonly type = TableActionTypes.ToggleSorting;

  constructor(public tableId: string, public enabled?: boolean) {}
}

export class TogglePagination implements Action {
  public readonly type = TableActionTypes.TogglePagination;

  constructor(public tableId: string, public enabled?: boolean) {}
}

export class RegisterTable<D> implements Action {
  public readonly type = TableActionTypes.RegisterTable;

  constructor(public tableId: string, public payload: Partial<TableState<D>>) {}
}

export class RegisterTables implements Action {
  public readonly type = TableActionTypes.RegisterTables;

  constructor(
    public tables: {
      [tableId: string]: Partial<TableState<any>>;
    }
  ) {}
}

export class ActionWithRow<R extends IWithIdentifier> {
  public rowId: string;

  constructor(
    public tableId: string,
    public row: R,
    public collectionPath: string
  ) {
    this.rowId = getRowId(row);
  }
}

export class DeleteRow<R extends IWithIdentifier = any> extends ActionWithRow<R>
  implements Action {
  public readonly type = TableActionTypes.DeleteRow;
}

export class EditRow<R extends IWithIdentifier = any> extends ActionWithRow<R>
  implements Action {
  public readonly type = TableActionTypes.EditRow;
}

export class ViewRow<R extends IWithIdentifier = any> extends ActionWithRow<R>
  implements Action {
  public readonly type = TableActionTypes.ViewRow;
}

export class DownloadRow<R extends IWithIdentifier = any>
  extends ActionWithRow<R>
  implements Action {
  public readonly type = TableActionTypes.DownloadRow;
}

export class SetExpandedRow<R extends IWithIdentifier = any>
  extends ActionWithRow<R>
  implements Action {
  public readonly type = TableActionTypes.SetExpandedRow;

  constructor(
    tableId: string,
    row: R,
    collectionPath: string,
    public editMode: boolean,
    public toggle?: boolean
  ) {
    super(tableId, row, collectionPath);
  }
}

export class ArchiveRow<R extends IWithIdentifier = any>
  extends ActionWithRow<R>
  implements Action {
  readonly type = TableActionTypes.ArchiveRow;
}

export class RestoreRow<R extends IWithIdentifier = any>
  extends ActionWithRow<R>
  implements Action {
  public readonly type = TableActionTypes.RestoreRow;
}

export class SelectRow<R extends IWithIdentifier = any> extends ActionWithRow<R>
  implements Action {
  public readonly type = TableActionTypes.SelectRow;
}

export class AddRowToSelection<R extends IWithIdentifier = any>
  extends ActionWithRow<R>
  implements Action {
  public readonly type = TableActionTypes.AddRowToSelection;
}

export class RemoveRowFromSelection<R extends IWithIdentifier = any>
  extends ActionWithRow<R>
  implements Action {
  public readonly type = TableActionTypes.RemoveRowFromSelection;
}

export class MoveRowUp<R extends IWithIdentifier = any> extends ActionWithRow<R>
  implements Action {
  public static TYPE = '@digitaix/tables/MOVE_ROW_UP';
  public readonly type = MoveRowUp.TYPE;
}

export class MoveRowDown<R extends IWithIdentifier = any>
  extends ActionWithRow<R>
  implements Action {
  public static TYPE = '@digitaix/tables/MOVE_ROW_DOWN';
  public readonly type = MoveRowDown.TYPE;
}

export abstract class IconButtonTableAction<Row> implements Action {
  public readonly type = (this.constructor as any).TYPE;

  public constructor(public tableId: string, public row: Row) {}
}

export type TableActions<D> =
  | ArchiveRow
  | RestoreRow
  | AddRowToSelection
  | RemoveRowFromSelection
  | RegisterTable<D>
  | SetExpandedRow
  | ToggleFiltering
  | TogglePagination
  | ToggleSorting
  | ExportTableData
  | ImportTableData
  | ToggleDownload
  | ToggleUpload
  | ToggleShowUpdatedAt
  | ToggleShowCreatedAt
  | ToggleShowArchived
  | ToggleColumnHidden
  | SetLoadingLimit
  | SetPage
  | ViewRow
  | DownloadRow
  | AddRow
  | SetCurrentTableTab
  | SetExpandedRowEditMode
  | ToggleDialogEdit
  | ToggleDialogView
  | SetExpandedRowFullMode
  | RegisterTables
  | ClearExpandedRowId
  | LoadTableData
  | ClearSelectedRows
  | SetPageSize
  | SetFilter<any>
  | EditRow
  | SetTableLimit
  | SelectRow;
